import { CarePlanCartItem } from '@/features/subscription/types/create-subscription.type';
import {
  CartProduct,
  MedicationWithCarePlanId
} from '@/features/subscription/types/subscription-cart.type';
import { calculateProcessingToCharge } from '@/features/subscription/utils/shared.util';
import { calculateDiscountAmount } from '@/features/subscription-management/utils.ts';
import { CarePlan, CarePlanFlat } from '@/models/care-plan.model';
import {
  MedicationInstructionsFrequency,
  MedicationInstructionsQuantity
} from '@/models/medication-instructions.model';
import { ProductFlat } from '@/models/product.model';
import { ShippingMethod } from '@/models/shipping-method.model';
import { Interval, IntervalEnum } from '@/types/common.type';
import {
  compareIntervals,
  convertIntervalToDays,
  convertIntervalToMonths
} from '@/utils/time.util';

export const getFastestShippingMethod = (
  allShippingMethods: ShippingMethod[]
): ShippingMethod => {
  return allShippingMethods.reduce<ShippingMethod>(
    (res, shippingMethod) => {
      if (!res || res.speedScore < shippingMethod.speedScore)
        return shippingMethod;

      return res;
    },
    null as unknown as ShippingMethod
  );
};

export const getAllMedicationsFromCarePlans = (
  carePlans: CarePlan[],
  excludedMedicationIds: string[] = []
): MedicationWithCarePlanId[] => {
  const medicationMap: Record<string, MedicationWithCarePlanId> = {};

  carePlans.forEach((carePlan) => {
    carePlan.medications.forEach((medication) => {
      if (excludedMedicationIds.includes(medication.id)) return;

      if (!medicationMap[medication.id]) {
        medicationMap[medication.id] = {
          ...medication,
          carePlanId: carePlan.id
        };
      }
    });
  });

  return Object.values(medicationMap);
};

/**
 * Calculate estimated supply size based on interval and instructions
 *
 * @param props.interval {Interval} - The interval for which to calculate supply
 * @param props.instructionsQuantity {MedicationInstructionsQuantity} - Quantity instructions
 * @param props.instructionsFrequency {MedicationInstructionsFrequency} - Frequency instructions
 * @returns {number} Estimated supply size needed for the interval
 */
export const calculateEstimatedSupplySize = (props: {
  interval: Interval;
  instructionsQuantity: MedicationInstructionsQuantity;
  instructionsFrequency: MedicationInstructionsFrequency;
}): number => {
  const { interval, instructionsQuantity, instructionsFrequency } = props;

  const intervalDays = convertIntervalToDays(interval);

  const { quantityForFrequencyInterval, frequencyIntervalDays } =
    instructionsFrequency.isPrn && instructionsFrequency.prnQuantityEstimation
      ? {
          quantityForFrequencyInterval:
            instructionsFrequency.prnQuantityEstimation,
          frequencyIntervalDays: 30
        }
      : {
          quantityForFrequencyInterval:
            instructionsQuantity.quantityMultiplier *
            instructionsFrequency.frequencyMultiplier,
          frequencyIntervalDays: convertIntervalToDays({
            unit: instructionsFrequency.intervalUnit,
            count: instructionsFrequency.intervalCount
          })
        };

  return (
    Math.round(intervalDays / frequencyIntervalDays) *
    quantityForFrequencyInterval
  );
};

/**
 * Find the best matching product for an estimated quantity
 * Returns the product with the smallest doseTotalValue that is >= estimatedQuantity
 * or null if none found
 *
 * @param products {ProductFlat[]} - List of available products
 * @param estimatedQuantity {number} - Estimated quantity needed
 * @param numberOfMonths {number} - Duration in months
 * @param isQtyMatchDuration {boolean} - Whether to enforce volume match
 * @returns {ProductFlat | null} Best matching product or null
 */
const findBestProduct = (
  products: ProductFlat[],
  estimatedQuantity: number,
  numberOfMonths: number,
  isQtyMatchDuration: boolean
): ProductFlat | null => {
  // Filter products that can fulfill the estimated quantity
  const suitableProducts = products.filter(
    (product) =>
      product.doseTotalValue >= estimatedQuantity &&
      (!isQtyMatchDuration || product.volume === numberOfMonths)
  );

  if (suitableProducts.length === 0) {
    return null;
  }

  // Sort by doseTotalValue ascending (smallest suitable product first)
  const sortedProducts = [...suitableProducts].sort(
    (a, b) => a.doseTotalValue - b.doseTotalValue
  );

  return sortedProducts[0];
};

/**
 * Deduplicate products - keep only the one with the maximum interval
 */
const deduplicateProductsByMaxInterval = (
  cartProducts: CartProduct[]
): CartProduct[] => {
  const productMap = new Map<string, CartProduct>();

  cartProducts.forEach((cartProduct) => {
    const existing = productMap.get(cartProduct.id);

    if (!existing) {
      // First time seeing this product
      productMap.set(cartProduct.id, cartProduct);
    } else {
      // Product already exists, keep the one with larger interval
      if (compareIntervals(cartProduct.interval, existing.interval) > 0) {
        productMap.set(cartProduct.id, cartProduct);
      }
    }
  });

  return Array.from(productMap.values());
};

/**
 * Generate available cart products from medication and parameters
 */
export const generateAvailableCartProducts = ({
  availableCarePlans,
  medication,
  instructionsQuantity,
  instructionsFrequency,
  selectedIntervals
}: {
  availableCarePlans: CarePlanFlat[];
  medication: MedicationWithCarePlanId | null;
  instructionsQuantity: MedicationInstructionsQuantity | null;
  instructionsFrequency: MedicationInstructionsFrequency | null;
  selectedIntervals: { [_carePlanId: string]: Interval };
}): CartProduct[] => {
  // Early return if required parameters are missing
  if (!medication || !instructionsQuantity || !instructionsFrequency) {
    return [];
  }

  const carePlan = availableCarePlans.find(
    (item) => item.id === medication.carePlanId
  ) as CarePlanFlat;

  // Separate products from medication for the cart product structure
  const { products, ...medicationFlat } = medication;

  const carePlanIntervals: Interval[] = (
    carePlan?.durationMonthsOptions || []
  ).map((carePlanInterval) => ({
    count: carePlanInterval,
    unit: IntervalEnum.MONTH
  }));

  const intervals = selectedIntervals[medication.carePlanId]
    ? [selectedIntervals[medication.carePlanId]]
    : carePlanIntervals;

  // Generate cart products for each interval
  const cartProducts: CartProduct[] = intervals
    .map((interval) => {
      // Const interval Months
      const intervalMonths = convertIntervalToMonths(interval);

      // Calculate how much medication is needed for this interval
      const estimatedQuantity = calculateEstimatedSupplySize({
        interval,
        instructionsQuantity,
        instructionsFrequency
      });

      // Find the best product that can fulfill this quantity
      const product = findBestProduct(
        products,
        estimatedQuantity,
        intervalMonths,
        medication.isQtyMatchDuration
      );

      if (!product) {
        return null as unknown as CartProduct;
      }

      // Calculate total cost (product + care plan for the interval)
      const total = product.unitAmount + carePlan.unitAmount * intervalMonths;

      // Build the cart product
      return {
        ...product,
        medication: medicationFlat,
        interval,
        total
      };
    })
    .filter((product) => product !== null);

  // Deduplicate: keep only the product with the maximum interval
  const deduplicatedProducts = deduplicateProductsByMaxInterval(cartProducts);

  // Sort by interval (descending) to show longest duration last
  return deduplicatedProducts.sort(
    (a, b) => -compareIntervals(b.interval, a.interval)
  );
};

/**
 * Calculate pricing breakdown for a care plan cart item
 *
 * @param cartItem {CarePlanCartItem} - The care plan cart item
 * @returns {Object} Pricing breakdown including subtotal, shipping, processing, discount, and total
 */
export const calculateCartItemPricing = (
  cartItem: CarePlanCartItem
): {
  subtotal: number;
  shippingAmount: number;
  processingAmount: number;
  discountAmount: number;
  total: number;
} => {
  const medicationAmount = cartItem.items.reduce(
    (res, item) => res + item.product.unitAmount,
    0
  );
  const carePlanAmount =
    cartItem.carePlan.unitAmount *
    convertIntervalToMonths(cartItem.billingInterval);

  const subtotal = medicationAmount + carePlanAmount;
  const shippingAmount = cartItem.shippingMethod.price;
  const discountAmount = cartItem.coupon
    ? calculateDiscountAmount(
        cartItem.coupon,
        carePlanAmount,
        subtotal,
        shippingAmount
      )
    : 0;
  const totalBeforeProcessing = subtotal + shippingAmount - discountAmount;
  const processingAmount = calculateProcessingToCharge(totalBeforeProcessing);
  const total = totalBeforeProcessing + processingAmount;

  return {
    subtotal,
    shippingAmount,
    processingAmount,
    discountAmount,
    total
  };
};
